import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  updateProfile as fbUpdateProfile,
  deleteUser,
  sendPasswordResetEmail,
  User as FirebaseUser,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db, removeUndefinedDeep } from './firebase';
import { apiFetch } from './apiConfig';
import type { MembershipStatus, PaymentStatus } from '../types';
import { validateAdultDob } from '../utils/dobUtils';

export interface ClientProfileData {
  uid: string;
  clientId: string;
  fullName: string;
  name: string;
  displayName: string;
  legalName?: string;
  email: string;
  phoneNumber: string;
  phone: string;
  gender?: 'Male' | 'Female' | string;
  dob?: string;
  age?: number;
  city: string;
  bio: string;
  avatarUrl: string;
  role: 'client';
  accountType: 'client';
  accountStatus?: 'Active' | 'Inactive' | 'Suspended';
  profileStatus: 'active' | 'inactive';
  isVerified: boolean;
  payment_verified: boolean;
  payment_status: PaymentStatus;
  membership_status: MembershipStatus;
  isCompletedRegistration: boolean;
  createdAt?: any;
  updatedAt?: any;
}

export interface ClientSignUpParams {
  email: string;
  password: string;
  fullName: string;
  displayName?: string;
  phone: string;
  gender?: 'Male' | 'Female' | string;
  dob?: string;
  city?: string;
  bio?: string;
  avatarUrl?: string;
}

/**
 * Generates a clean, unique, collision-resistant Client ID derived from UID.
 * Example: CL-A7F92C3D
 */
export function generatePermanentClientId(uid: string): string {
  if (!uid) return `CL-${Math.random().toString(36).substring(2, 10).toUpperCase()}`;
  const clean = uid.replace(/[^a-zA-Z0-9]/g, '');
  const prefix = clean.length >= 8 ? clean.substring(0, 8).toUpperCase() : clean.padEnd(8, '0').toUpperCase();
  return `CL-${prefix}`;
}

/**
 * Creates a new Client in Firebase Authentication and stores permanent profile in Firestore users/{uid}.
 * Keeps the user authenticated automatically — no re-login needed!
 */
export async function clientSignUp(params: ClientSignUpParams): Promise<{
  user: FirebaseUser;
  profile: ClientProfileData;
}> {
  const { email, password, fullName, displayName, phone, gender, dob, city, bio, avatarUrl } = params;

  // Validation
  const trimmedEmail = (email || '').trim().toLowerCase();
  const trimmedName = (fullName || '').trim();
  const trimmedDisplayName = (displayName || '').trim() || trimmedName.split(' ')[0] || 'Client';
  const trimmedPhone = (phone || '').trim().replace(/\D/g, '');
  const normalizedGender = gender ? String(gender).trim() : undefined;

  if (!trimmedEmail || !trimmedEmail.includes('@')) {
    throw new Error('Please enter a valid email address.');
  }
  if (!trimmedName || trimmedName.length < 2) {
    throw new Error('Please enter your full legal name.');
  }
  if (!password || password.length < 6) {
    throw new Error('Password must be at least 6 characters long.');
  }
  if (!trimmedPhone || trimmedPhone.length < 10) {
    throw new Error('Please enter a valid 10-digit mobile phone number.');
  }

  let normalizedDob: string | undefined;
  let calculatedAge: number | undefined;
  if (dob !== undefined && String(dob).trim() !== '') {
    const dobResult = validateAdultDob(dob, new Date(), normalizedGender);
    if (!dobResult.valid) {
      throw new Error(dobResult.error || 'Not eligible — your age does not meet the minimum requirement.');
    }
    normalizedDob = dobResult.normalizedDob;
    calculatedAge = dobResult.age ?? undefined;
  }

  // 1. Create Firebase Authentication Account
  let user: FirebaseUser | any;
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, trimmedEmail, password);
    user = userCredential.user;

    // 2. Set Display Name on Auth Profile
    try {
      await fbUpdateProfile(user, {
        displayName: trimmedDisplayName,
        photoURL: avatarUrl || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=400&q=80',
      });
    } catch (err) {
      console.warn('Failed to update Firebase Auth display name:', err);
    }
  } catch (authErr: any) {
    throw authErr;
  }

  // 3. Generate permanent unique Client ID
  const clientId = generatePermanentClientId(user.uid);

  // 4. Construct Firestore Document Data strictly complying with Security Rules
  const profileData: ClientProfileData = {
    uid: user.uid,
    clientId,
    fullName: trimmedName,
    name: trimmedDisplayName,
    displayName: trimmedDisplayName,
    legalName: trimmedName,
    email: trimmedEmail,
    phoneNumber: trimmedPhone,
    phone: trimmedPhone,
    gender: normalizedGender,
    dob: normalizedDob,
    age: calculatedAge,
    city: (city || 'Kolkata, West Bengal').trim(),
    bio: (bio || 'Verified Client on PairUpToday. Excited to meet respectful social companions for dinners, city tours, and events.').trim(),
    avatarUrl: avatarUrl || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=400&q=80',
    role: 'client',
    accountType: 'client',
    accountStatus: 'Active',
    profileStatus: 'active',
    isVerified: false,
    payment_verified: false,
    payment_status: 'Pending',
    membership_status: 'INACTIVE',
    isCompletedRegistration: true,
  };

  const userDocRef = doc(db, 'users', user.uid);
  const { accountStatus: _omittedAccountStatus, ...firestoreSafeProfile } = profileData;
  const payloadToStore = removeUndefinedDeep({
    ...firestoreSafeProfile,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  try {
    await setDoc(userDocRef, payloadToStore, { merge: true });
  } catch (fsErr) {
    console.error('Firestore user profile creation failed:', fsErr);
    throw new Error(
      'Your account was created, but your profile could not be saved. Please try signing in again or contact support.'
    );
  }

  // 5. Save local storage backup for instant offline navigation
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('pairup_user', trimmedDisplayName);
      localStorage.setItem('pairup_user_profile_data', JSON.stringify(profileData));
      localStorage.setItem('pairup_client_id', clientId);
    }
  } catch (e) {
    console.warn('Local storage error:', e);
  }

  return { user, profile: profileData };
}

/**
 * Authenticates an existing Client or Companion using Email OR 10-digit Mobile Number and Password
 */
export async function clientLogin(
  emailOrMobile: string,
  pass: string,
  expectedRole?: 'client' | 'companion'
): Promise<{
  user: FirebaseUser;
  profile: ClientProfileData;
}> {
  const rawIdentifier = (emailOrMobile || '').trim();
  let trimmedEmail = rawIdentifier.toLowerCase();

  // Support direct login with 10-digit registered mobile number + password
  if (!trimmedEmail.includes('@')) {
    const cleanPhone = rawIdentifier.replace(/\D/g, '').slice(-10);
    if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
      throw new Error('Please enter a valid email address or 10-digit Indian mobile number.');
    }

    let resolvedEmail = '';
    try {
      const { apiFetch } = await import('./apiConfig');
      const res = await apiFetch('/api/v1/auth/resolve-mobile-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: cleanPhone, role: expectedRole }),
      });
      const data = await res.json();
      if (res.ok && data?.success && data?.email) {
        resolvedEmail = String(data.email).trim().toLowerCase();
      } else if (data?.error?.message) {
        throw new Error(data.error.message);
      }
    } catch (resolveErr: any) {
      if (
        resolveErr?.message &&
        !resolveErr.message.includes('Failed to fetch') &&
        !resolveErr.message.includes('temporarily unavailable')
      ) {
        throw resolveErr;
      }
    }

    if (!resolvedEmail) {
      // Fallback: check Firestore users collection by phone/phoneNumber
      try {
        const candidates = [cleanPhone, `+91${cleanPhone}`, `91${cleanPhone}`];
        for (const field of ['phone', 'phoneNumber']) {
          const q = query(collection(db, 'users'), where(field, 'in', candidates));
          const snap = await getDocs(q);
          if (!snap.empty) {
            const docData = snap.docs[0].data();
            if (docData?.email) {
              resolvedEmail = String(docData.email).trim().toLowerCase();
              break;
            }
          }
        }
      } catch {
        // Ignore Firestore query fallback errors
      }
    }

    if (!resolvedEmail) {
      const err = new Error('No registered account was found for this mobile number. Please sign up first.');
      (err as any).code = 'ACCOUNT_NOT_FOUND';
      throw err;
    }

    trimmedEmail = resolvedEmail;
  }

  let user: FirebaseUser | any;
  try {
    const userCredential = await signInWithEmailAndPassword(auth, trimmedEmail, pass);
    user = userCredential.user;
  } catch (authErr: any) {
    throw authErr;
  }

  // Retrieve existing account profile from Firestore (never auto-create on login)
  const userDocRef = doc(db, 'users', user.uid);
  const snap = await getDoc(userDocRef);

  if (!snap.exists()) {
    await fbSignOut(auth).catch(() => {});
    const err = new Error('No registered account profile found. Please sign up first.');
    (err as any).code = 'ACCOUNT_NOT_FOUND';
    throw err;
  }

  const data = snap.data();
  const rawRole = String(data.role || data.accountType || 'client').toLowerCase();
  if (rawRole === 'admin' || rawRole === 'super_admin' || rawRole === 'superadmin') {
    await fbSignOut(auth).catch(() => {});
    const err = new Error('Administrator accounts must sign in via the Admin Login portal.');
    (err as any).code = 'ADMIN_LOGIN_METHOD_REQUIRED';
    throw err;
  }

  const accountStatus = String(data.accountStatus || 'Active').toLowerCase();
  if (['suspended', 'disabled', 'deleted', 'blocked'].includes(accountStatus)) {
    await fbSignOut(auth).catch(() => {});
    const err = new Error('This account is suspended or unavailable. Please contact support.');
    (err as any).code = 'ACCOUNT_UNAVAILABLE';
    throw err;
  }

  const actualRole: 'client' | 'companion' =
    data.accountType === 'companion' || rawRole.includes('companion') ? 'companion' : 'client';
  if (expectedRole && actualRole !== expectedRole) {
    await fbSignOut(auth).catch(() => {});
    const err = new Error(
      `Role mismatch: This account is registered as a ${actualRole === 'companion' ? 'Companion' : 'Client'}. Please switch to the ${actualRole === 'companion' ? 'Companion Host' : 'Client'} login tab.`
    );
    (err as any).code = 'ROLE_MISMATCH';
    throw err;
  }

  const clientId = data.clientId || generatePermanentClientId(user.uid);
  const profile: ClientProfileData = {
    ...data,
    uid: user.uid,
    clientId,
    fullName: data.fullName || data.legalName || data.name || user.displayName || 'Client',
    name: data.displayName || data.name || user.displayName || 'Client',
    displayName: data.displayName || data.name || user.displayName || 'Client',
    email: data.email || user.email || trimmedEmail,
    phoneNumber: data.phoneNumber || data.phone || '',
    phone: data.phoneNumber || data.phone || '',
    city: data.city || 'Kolkata, West Bengal',
    bio: data.bio || '',
    avatarUrl: data.avatarUrl || user.photoURL || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=400&q=80',
    role: actualRole as any,
    accountType: actualRole as any,
    accountStatus: data.accountStatus || 'Active',
    profileStatus: data.profileStatus || 'active',
    isVerified: Boolean(data.isVerified),
    payment_verified: Boolean(data.payment_verified),
    payment_status: data.payment_status || 'Pending',
    membership_status: data.membership_status || 'INACTIVE',
    isCompletedRegistration: true,
  } as ClientProfileData;

  if (!data.clientId) {
    await updateDoc(userDocRef, { clientId, updatedAt: serverTimestamp() }).catch(() => {});
  }

  // Sync to local cache
  try {
    localStorage.setItem('pairup_user', profile.displayName);
    localStorage.setItem('pairup_user_profile_data', JSON.stringify(profile));
    localStorage.setItem('pairup_client_id', profile.clientId);
  } catch {}

  return { user, profile };
}

/**
 * Updates permitted personal fields of the authenticated Client
 */
export async function updateClientProfile(fields: {
  fullName?: string;
  displayName?: string;
  phoneNumber?: string;
  city?: string;
  bio?: string;
  avatarUrl?: string;
}): Promise<void> {
  const currentAuthUser = auth.currentUser;
  if (!currentAuthUser) {
    throw new Error('You must be signed in to update your profile.');
  }

  const updates: Record<string, any> = {
    updatedAt: serverTimestamp(),
  };

  if (fields.fullName !== undefined) {
    updates.fullName = fields.fullName.trim();
    updates.legalName = fields.fullName.trim();
  }
  if (fields.displayName !== undefined) {
    updates.displayName = fields.displayName.trim();
    updates.name = fields.displayName.trim();
  }
  if (fields.phoneNumber !== undefined) {
    updates.phoneNumber = fields.phoneNumber.trim();
    updates.phone = fields.phoneNumber.trim();
  }
  if (fields.city !== undefined) updates.city = fields.city.trim();
  if (fields.bio !== undefined) updates.bio = fields.bio.trim();
  if (fields.avatarUrl !== undefined) updates.avatarUrl = fields.avatarUrl;

  const userDocRef = doc(db, 'users', currentAuthUser.uid);
  await updateDoc(userDocRef, removeUndefinedDeep(updates));

  // Sync Auth displayName
  if (fields.displayName || fields.avatarUrl) {
    try {
      await fbUpdateProfile(currentAuthUser, {
        displayName: fields.displayName || currentAuthUser.displayName || undefined,
        photoURL: fields.avatarUrl || currentAuthUser.photoURL || undefined,
      });
    } catch {}
  }
}

/**
 * Checks if the client has any active/pending bookings that block account deletion
 */
export async function checkClientDeletionEligibility(clientUid: string): Promise<{
  eligible: boolean;
  activeCount: number;
  message?: string;
}> {
  if (!clientUid) return { eligible: false, activeCount: 0, message: 'Invalid UID' };

  try {
    const bookingsCol = collection(db, 'bookings');
    const q1 = query(
      bookingsCol,
      where('clientId', '==', clientUid),
      where('status', 'in', ['pending', 'confirmed', 'PENDING', 'CONFIRMED'])
    );
    const snap = await getDocs(q1);

    if (!snap.empty) {
      return {
        eligible: false,
        activeCount: snap.size,
        message: `You currently have ${snap.size} active or pending booking(s). Please complete or cancel them before deleting your account.`,
      };
    }

    return { eligible: true, activeCount: 0 };
  } catch (e) {
    console.warn('Check deletion eligibility notice:', e);
    return { eligible: true, activeCount: 0 };
  }
}

/**
 * Safely deletes client profile and authentication account
 */
export async function deleteClientAccount(
  currentPasswordOrReason?: string
): Promise<{ success: boolean; message: string; error?: string }> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user session found.');
  }

  // 1. Verify eligibility
  const check = await checkClientDeletionEligibility(user.uid);
  if (!check.eligible) {
    return {
      success: false,
      message: check.message || 'Cannot delete account with active bookings.',
      error: check.message || 'Cannot delete account with active bookings.',
    };
  }

  // 2. Mark account deleted or delete Firestore record
  const userDocRef = doc(db, 'users', user.uid);
  try {
    await deleteDoc(userDocRef);
  } catch (err) {
    console.warn('Firestore doc delete note:', err);
  }

  // 3. Delete Firebase Auth User
  try {
    await deleteUser(user);
  } catch (authErr: any) {
    if (authErr?.code === 'auth/requires-recent-login') {
      throw new Error('For security reasons, please log in again before deleting your account.');
    }
    throw authErr;
  }

  // 4. Clear local cache
  try {
    localStorage.removeItem('pairup_user');
    localStorage.removeItem('pairup_user_profile_data');
    localStorage.removeItem('pairup_client_id');
  } catch {}

  return { success: true, message: 'Your PairUpToday client account has been safely deleted.' };
}

/**
 * Sends a password reset SMS OTP via APITXT to the registered Indian mobile number
 */
export async function sendPasswordResetMobileOtp(mobile: string): Promise<{
  success: boolean;
  otpId?: string;
  verificationId?: string;
  cooldownSeconds?: number;
  message?: string;
  error?: string;
}> {
  const cleanPhone = String(mobile || '').replace(/\D/g, '').slice(-10);
  if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
    return {
      success: false,
      error: 'Please enter a valid 10-digit Indian mobile number.',
    };
  }
  try {
    const res = await apiFetch('/api/v1/auth/password-reset/send-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mobile: cleanPhone }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success || !data.verificationId) {
      return {
        success: false,
        error: data?.error?.message || data?.message || 'Could not send password reset OTP.',
      };
    }
    return {
      success: true,
      otpId: data.verificationId,
      verificationId: data.verificationId,
      cooldownSeconds: data.cooldownSeconds || 60,
      message: data.message || 'Password reset OTP sent via APITXT SMS.',
    };
  } catch (err: any) {
    return {
      success: false,
      error: err?.message || 'Password reset OTP service is temporarily unavailable.',
    };
  }
}

/**
 * Confirms password reset with APITXT SMS OTP and updates Firebase Auth password
 */
export async function confirmPasswordResetWithMobileOtp(
  mobile: string,
  verificationId: string,
  otp: string,
  newPassword: string
): Promise<{
  success: boolean;
  email?: string;
  message?: string;
  error?: string;
}> {
  const cleanPhone = String(mobile || '').replace(/\D/g, '').slice(-10);
  const cleanOtp = String(otp || '').replace(/\D/g, '').slice(0, 6);
  try {
    const res = await apiFetch('/api/v1/auth/password-reset/confirm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mobile: cleanPhone,
        verificationId,
        otp: cleanOtp,
        newPassword,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      return {
        success: false,
        error: data?.error?.message || data?.message || 'Failed to reset password with OTP.',
      };
    }
    return {
      success: true,
      email: data.email,
      message: data.message || 'Password reset completed via APITXT OTP!',
    };
  } catch (err: any) {
    return {
      success: false,
      error: err?.message || 'Unable to complete password reset.',
    };
  }
}

/**
 * Sends a password reset email via Firebase Authentication
 */
export async function sendClientPasswordReset(email: string): Promise<void> {
  const cleanEmail = (email || '').trim().toLowerCase();
  if (!cleanEmail || !cleanEmail.includes('@')) {
    throw new Error('Please enter a valid email address for password reset.');
  }
  await sendPasswordResetEmail(auth, cleanEmail);
}

/**
 * Signs the client out securely
 */
export async function clientLogout(): Promise<void> {
  try {
    await fbSignOut(auth);
  } finally {
    try {
      localStorage.removeItem('pairup_user');
      localStorage.removeItem('pairup_user_profile_data');
      localStorage.removeItem('pairup_client_id');
    } catch {}
  }
}
